import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { LevelIcon } from "../LevelIcon";
import { DURATION_OPTIONS } from "./durationOptions";

export { DURATION_OPTIONS, type DurationOption } from "./durationOptions";

type Props = {
	options?: readonly number[];
	selected?: number;
	disabled?: boolean;
	formatLabel: (minutes: number) => string;
	onSelect: (minutes: number) => void;
};

type CellProps = {
	minutes: number;
	selected: boolean;
	disabled?: boolean;
	formatLabel: (minutes: number) => string;
	onSelect: (minutes: number) => void;
};

const DurationCell = ({
	minutes,
	selected,
	disabled,
	formatLabel,
	onSelect,
}: CellProps) => {
	const theme = useAppTheme();
	const label = formatLabel(minutes);

	return (
		<Pressable
			onPress={() => onSelect(minutes)}
			disabled={disabled}
			accessibilityRole="button"
			accessibilityState={{ selected }}
			accessibilityLabel={label}
			style={{ flex: 1, opacity: disabled ? 0.5 : 1 }}
		>
			<Box
				flex={1}
				flexDirection="row"
				alignItems="center"
				justifyContent="center"
				gap={8}
				paddingVertical={12}
				paddingHorizontal={8}
				borderWidth={1}
				borderRadius={theme.roundness * 2}
				borderColor={
					selected ? theme.colors.secondary : theme.colors.surfaceVariant
				}
				backgroundColor={
					selected ? theme.colors.elevation.level5 : "transparent"
				}
			>
				{selected && (
					<LevelIcon
						name="clock-outline"
						size={18}
						color={theme.colors.onSurface}
					/>
				)}
				<Text
					variant="titleSmall"
					numberOfLines={1}
					style={{ color: theme.colors.onSurface }}
				>
					{label}
				</Text>
			</Box>
		</Pressable>
	);
};

const DurationSelector = ({
	options = DURATION_OPTIONS,
	selected,
	disabled,
	formatLabel,
	onSelect,
}: Props) => {
	return (
		<Box flexDirection="row" gap={8}>
			{options.map((minutes) => (
				<DurationCell
					key={minutes}
					minutes={minutes}
					selected={selected === minutes}
					disabled={disabled}
					formatLabel={formatLabel}
					onSelect={onSelect}
				/>
			))}
		</Box>
	);
};

export default DurationSelector;
